import { Component, lazy, Suspense, useEffect, type ErrorInfo, type ReactNode } from "react";
import { Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { LEGACY_VILLAGR_PATHS, legacyVillagrTarget } from "@/apps/games/villagr/legacy-route";

import { ShellLayout } from "@/shell/shell-layout";
import { AuthGuard } from "@/shell/components/auth-guard";
import { NotesLayout } from "@/apps/notes/components/notes-layout";
import { BriefLayout } from "@/apps/brief/components/brief-layout";
import { findAppByRoute } from "@/shell/app-registry";
import { gameAppForPath } from "@/shell/game-apps";
import { useOpenAppsStore } from "@/stores/open-apps-store";
import { recoverFromChunkLoad, resetApp } from "@/utils/reload-guard";

function isChunkLoadError(error: unknown) {
  const message = error instanceof Error ? error.message : String(error);
  return /Failed to fetch dynamically imported module|Loading chunk|Importing a module script failed|module script/i.test(message);
}

/**
 * Backoff before giving up on a chunk, in ms.
 *
 * Two separate things go wrong after a deploy and the backoff only covers the first: a brief window where
 * the edge serving the new index.html does not yet have its sibling assets. Waiting that out is cheap and
 * costs nothing when the chunk is already there.
 *
 * It is NOT what made recovery loop — see repairChunkCache for the mechanism that did, and note that a
 * retry without that repair cannot succeed no matter how long the backoff is.
 */
const CHUNK_BACKOFF = [400, 1200, 3000, 5000];
const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/**
 * Overwrite the browser's stored response for a chunk URL, then report whether the network has it.
 *
 * This is the actual mechanism behind the whole post-deploy failure, measured rather than guessed:
 *
 *   1. `_redirects` ends in `/* /index.html 200`, so a request for an asset the edge does not have yet
 *      does not 404 — it returns **200 text/html**. Confirmed live: /assets/definitely-missing-file.js
 *      returns 7901 bytes of index.html.
 *   2. That response carries `cache-control: public, max-age=0, must-revalidate`, so the browser STORES
 *      it against the asset's URL and revalidates by ETag. index.html's ETag does not change, so every
 *      revalidation returns 304 and the browser keeps serving the stored HTML for a URL that is supposed
 *      to be JavaScript.
 *   3. So the poisoned entry outlives everything: retrying the import, reloading the page, purging the
 *      service worker. `_fresh` only cache-busts the NAVIGATION url, never the asset's. Meanwhile curl,
 *      which stores nothing, fetched the very same URLs correctly — which is what made this look like a
 *      server problem for so long.
 *
 * `cache: "reload"` is the fix: it forces a network round trip AND replaces the stored entry, so the next
 * import (or the reload that follows) sees JavaScript.
 */
async function repairChunkCache(error: unknown): Promise<void> {
  const msg = error instanceof Error ? error.message : String(error);
  const url = /(https?:\/\/[^\s)]+\.m?js)/.exec(msg)?.[1];
  if (!url) return;
  try {
    const r = await fetch(url, { cache: "reload" });
    const type = r.headers.get("content-type") ?? "";
    if (!r.ok || type.includes("text/html")) console.warn(`[chunk] ${url} still not JavaScript (${r.status} ${type})`);
  } catch { /* offline or blocked — the reload fallback still applies */ }
}

async function loadChunk<T>(loader: () => Promise<T>, attempt = 0): Promise<T> {
  try {
    return await loader();
  } catch (error) {
    if (!isChunkLoadError(error)) throw error;
    if (attempt < CHUNK_BACKOFF.length) {
      // Logged because the alternative is guessing: a silent retry is indistinguishable from a slow
      // network, and the first pass at this bug was diagnosed three wrong ways for exactly that reason.
      console.warn(`[chunk] retry ${attempt + 1}/${CHUNK_BACKOFF.length} in ${CHUNK_BACKOFF[attempt]}ms`, error);
      await sleep(CHUNK_BACKOFF[attempt]);
      // Repair the stored response BEFORE re-importing, or every retry re-reads the same cached HTML —
      // which is exactly what the console showed: 4 of 4 retries failing on a URL curl served fine.
      await repairChunkCache(error);
      return loadChunk(loader, attempt + 1);
    }
    // Retrying the import is the cheap fix — no reload, no lost state — but a browser is allowed to
    // memoize a failed module fetch, in which case re-importing the same specifier can never succeed and
    // only a fresh document will do. Hence the reload fallback stays; the backoff above has already put it
    // clear of the propagation window either way. purge + cache-bust: see recoverFromChunkLoad.
    if (await recoverFromChunkLoad()) return new Promise<T>(() => {}); // reload in flight — keep the fallback up
    throw error;
  }
}

function lazyWithReload<T extends { default: React.ComponentType<unknown> }>(loader: () => Promise<T>) {
  return lazy(() => loadChunk(loader));
}

class ChunkErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean; chunk: boolean; recovering: boolean; msg: string }> {
  state = { hasError: false, chunk: false, recovering: false, msg: "" };

  // Only a CHUNK error triggers a reload, but the fallback used to claim "Refreshing Slumbr..." with a
  // spinner for EVERY error — so a plain runtime crash (a bad render in a game/app) sat forever behind a
  // progress animation for something that was never going to happen. Remember which kind it was.
  //
  // `recovering` starts optimistic for a chunk error and is cleared if the breaker refuses to reload:
  // the spinner must only ever be shown while a reload is genuinely coming.
  static getDerivedStateFromError(error: unknown) {
    const chunk = isChunkLoadError(error);
    return { hasError: true, chunk, recovering: chunk, msg: error instanceof Error ? error.message : String(error) };
  }

  componentDidCatch(error: unknown, info: ErrorInfo) {
    console.error(isChunkLoadError(error) ? "Route chunk failed" : "Route crashed", error, info);
    // The reload lives here, not in getDerivedStateFromError: that hook must stay pure (React may call it
    // more than once), and the purge it now needs is async.
    if (!isChunkLoadError(error)) return;
    // This path never had the loader, so it cannot re-import — but it CAN repair the poisoned cache entry
    // first (see repairChunkCache), which is what makes the reload that follows able to succeed at all.
    void repairChunkCache(error)
      .then(recoverFromChunkLoad)
      .then((reloading) => { if (!reloading) this.setState({ recovering: false }); });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex h-screen items-center justify-center bg-[#0b0d0f] px-6 text-center text-[#e7e9ea]">
          <div className="max-w-md">
            {this.state.chunk && this.state.recovering ? (
              <>
                <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-2 border-[#ff477e] border-t-transparent" />
                <p className="text-sm font-semibold">Refreshing Slumbr...</p>
              </>
            ) : this.state.chunk ? (
              // Breaker tripped: reloading again would not help, so say that instead of spinning forever.
              <>
                <p className="text-sm font-semibold">Slumbr couldn&apos;t load this page&apos;s new version.</p>
                <p className="mt-1 text-xs text-[#8b9199]">Reloading didn&apos;t clear it. Reset clears the offline cache and starts fresh.</p>
              </>
            ) : (
              <>
                <p className="text-sm font-semibold">Something went wrong.</p>
                <p className="mt-1 break-words text-xs text-[#8b9199]">{this.state.msg || "Unexpected error"}</p>
              </>
            )}
            <div className="mt-3 flex items-center justify-center gap-2">
              {!this.state.recovering && (
                <button type="button" onClick={() => window.location.reload()} className="rounded-full border border-white/10 px-4 py-2 text-xs font-bold text-[#e7e9ea] hover:border-white/30">
                  Reload
                </button>
              )}
              <button type="button" onClick={() => void resetApp()} className="rounded-full border border-white/10 px-4 py-2 text-xs font-bold text-[#ff8fac] hover:border-[#ff6b8f]/60">
                Reset &amp; reload
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function ChunkReloadReset() {
  // The rapid-reload tally is cleared globally once the app stays up
  // (see markAppStable in main.tsx); nothing to do per-route now.
  return null;
}

// Messenger App Imports
import { MessengerWithPinLock } from "@/apps/messenger/routes";
const MessengerHome = lazyWithReload(() => import("@/apps/messenger/pages/messenger-home"))
const ConversationPage = lazyWithReload(() => import("@/apps/messenger/pages/conversation-page"))

// Communities App Imports
import { CommunitiesLayout } from "@/apps/communities/components/communities-layout";
const CommunityHome = lazyWithReload(() => import("@/apps/communities/pages/community-home"))
const CreateCommunity = lazyWithReload(() => import("@/apps/communities/pages/create-community"))
const CommunityPage = lazyWithReload(() => import("@/apps/communities/pages/community-page"))
const CommunitySettings = lazyWithReload(() => import("@/apps/communities/pages/community-settings"))
const JoinCommunity = lazyWithReload(() => import("@/apps/communities/pages/join-community"))

const LoginPage = lazyWithReload(() => import("@/shell/pages/login"));
const SignupPage = lazyWithReload(() => import("@/shell/pages/signup"));
const ResetPasswordPage = lazyWithReload(() => import("@/shell/pages/reset-password"));
const HomePage = lazyWithReload(() => import("@/shell/pages/home"));
const FeedPage = lazyWithReload(() => import("@/shell/pages/feed"));
const ProfilePage = lazyWithReload(() => import("@/shell/pages/profile"));
const AccountSettingsPage = lazyWithReload(() => import("@/shell/pages/account-settings"));
const NotesHome = lazyWithReload(() => import("@/apps/notes/pages/notes-home"));
const NotePage = lazyWithReload(() => import("@/apps/notes/pages/note-page"));
const GamesHome = lazyWithReload(() => import("@/apps/games/pages/games-home"));
const GameDetails = lazyWithReload(() => import("@/apps/games/pages/game-details"));
const GameEmbed = lazyWithReload(() => import("@/apps/games/embed/game-embed"));
const MimicMenu = lazyWithReload(() => import("@/apps/games/mimic/mimic-menu"));
const MimicRoom = lazyWithReload(() => import("@/apps/games/mimic/mimic-room"));
const MimicLocal = lazyWithReload(() => import("@/apps/games/mimic/local/local-game"));
const BlackjackMenu = lazyWithReload(() => import("@/apps/games/blackjack/blackjack-menu"));
const BlackjackTable = lazyWithReload(() => import("@/apps/games/blackjack/blackjack-table"));
const HiloMenu = lazyWithReload(() => import("@/apps/games/hilo/hilo-menu"));
const HiloTable = lazyWithReload(() => import("@/apps/games/hilo/hilo-table"));
// Card Table v2 (server-authoritative TableDO): lobby + table. The whole table UI is one lazy chunk,
// so nothing mid-game has to fetch a chunk (a stale-chunk recovery would reload the tab).
const TabletopLobby = lazyWithReload(() => import("@/apps/games/tabletop/lobby/lobby-page"));
const TabletopTable = lazyWithReload(() => import("@/apps/games/tabletop/ui/table-page"));
const ZingrMenu = lazyWithReload(() => import("@/apps/games/zingr/zingr-menu"));
const ZingrRoom = lazyWithReload(() => import("@/apps/games/zingr/zingr-room"));
const PokerMenu = lazyWithReload(() => import("@/apps/games/poker/poker-menu"));
const PokerTable = lazyWithReload(() => import("@/apps/games/poker/poker-table"));
const DoodlrMenu = lazyWithReload(() => import("@/apps/games/doodlr/doodlr-menu"));
const DoodlrRoom = lazyWithReload(() => import("@/apps/games/doodlr/doodlr-room"));
const RiddlrPage = lazyWithReload(() => import("@/apps/games/riddlr/riddlr-page"));
const BluffrMenu = lazyWithReload(() => import("@/apps/games/bluffr/bluffr-menu"));
const BluffrRoom = lazyWithReload(() => import("@/apps/games/bluffr/bluffr-room"));
const VillagrGame = lazyWithReload(() => import("@/apps/games/villagr/villagr-game"));
const VillagrStudio = lazyWithReload(() => import("@/apps/games/villagr/studio/studio-app"));
const HealthApp = lazyWithReload(() => import("@/apps/health/health-app"));
const BriefHome = lazyWithReload(() => import("@/apps/brief/pages/brief-home"));
const BriefProjectPage = lazyWithReload(() => import("@/apps/brief/pages/project-page"));
const BriefBoardPage = lazyWithReload(() => import("@/apps/brief/pages/board-page"));
const BriefMyWork = lazyWithReload(() => import("@/apps/brief/pages/my-work"));
const BriefInviteAccept = lazyWithReload(() => import("@/apps/brief/pages/invite-accept"));
const BriefPortfolio = lazyWithReload(() => import("@/apps/brief/pages/portfolio"));
const BriefPortal = lazyWithReload(() => import("@/apps/brief/pages/portal"));
const BriefSettings = lazyWithReload(() => import("@/apps/brief/components/workspace-settings"));

const AuthFallback = (
  <div className="flex h-screen items-center justify-center bg-surface-0 dark:bg-surface-dark-0">
    <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
  </div>
);

/** Legacy redirect: /games/quiplip/rooms/:roomId → /games/zingr/rooms/:roomId */
function QuiplipRoomRedirect() {
  const { roomId } = useParams<{ roomId: string }>();
  return <Navigate to={`/games/zingr/rooms/${roomId ?? ""}`} replace />;
}

/** F7: legacy settlr paths → /games/villagr (query + hash kept). The edge _redirects 301s these first; this covers a
 *  cached SPA shell or an in-app link. */
function SettlrRedirect() {
  const { search, hash } = useLocation();
  return <Navigate to={legacyVillagrTarget(search, hash)} replace />;
}

/** Syncs route changes with the open apps store */
function RouteAppSync() {
  const location = useLocation();
  const openApp = useOpenAppsStore((s) => s.openApp);
  const updateLastRoute = useOpenAppsStore((s) => s.updateLastRoute);

  useEffect(() => {
    // A specific game opens as its own app (own icon/tab), separate from the
    // Games store. The bare `/games` store stays the "Games" app.
    const gameApp = gameAppForPath(location.pathname);
    if (gameApp) {
      openApp(gameApp, location.pathname);
      updateLastRoute(gameApp.id, location.pathname);
      return;
    }
    const app = findAppByRoute(location.pathname);
    if (app) {
      openApp(app, location.pathname);
      updateLastRoute(app.id, location.pathname);
    }
  }, [location.pathname, openApp, updateLastRoute]);

  return null;
}

export function AppRouter() {
  return (
    <>
      <RouteAppSync />
      <ChunkReloadReset />
      <ChunkErrorBoundary>
      <Routes>
        {/* Public auth routes */}
        <Route
          path="/login"
          element={
            <Suspense fallback={AuthFallback}>
              <LoginPage />
            </Suspense>
          }
        />
        <Route
          path="/signup"
          element={
            <Suspense fallback={AuthFallback}>
              <SignupPage />
            </Suspense>
          }
        />
        <Route
          path="/reset-password"
          element={
            <Suspense fallback={AuthFallback}>
              <ResetPasswordPage />
            </Suspense>
          }
        />
        {/* Public read-only Brief client portal (no auth required) */}
        <Route
          path="/brief/portal/:token"
          element={
            <Suspense fallback={AuthFallback}>
              <BriefPortal />
            </Suspense>
          }
        />

        {/* Protected routes */}
        <Route element={<AuthGuard />}>
          {/* Villagr Studio — full-screen browser editor (no app shell). Served on villagr-studio.slumbr.xyz */}
          <Route path="/villagr-studio" element={<Suspense fallback={AuthFallback}><VillagrStudio /></Suspense>} />
          <Route element={<ShellLayout />}>
            <Route path="/home" element={<HomePage />} />
            <Route path="/feed" element={<FeedPage />} />
            <Route path="/health" element={<Suspense fallback={AuthFallback}><HealthApp /></Suspense>} />
            <Route path="/settings" element={<AccountSettingsPage />} />
            <Route path="/profile/:userId" element={<ProfilePage />} />

            {/* Notes App */}
            <Route path="/notes" element={<NotesLayout />}>
              <Route index element={<NotesHome />} />
              <Route path=":noteId" element={<NotePage />} />
            </Route>

            {/* Brief App */}
            <Route path="/brief" element={<BriefLayout />}>
              <Route index element={<BriefHome />} />
              <Route path="my-work" element={<BriefMyWork />} />
              <Route path="settings" element={<BriefSettings />} />
              <Route path="portfolio" element={<BriefPortfolio />} />
              <Route path="invite/:token" element={<BriefInviteAccept />} />
              <Route path="projects/:projectId" element={<BriefProjectPage />} />
              <Route path="boards/:boardId" element={<BriefBoardPage />} />
            </Route>

            {/* Messenger App */}
            <Route path="/messenger" element={<MessengerWithPinLock />}>
              <Route index element={<MessengerHome />} />
              <Route path=":conversationId" element={<ConversationPage />} />
            </Route>

            {/* Communities App */}
            <Route path="/communities" element={<CommunitiesLayout />}>
              <Route index element={<CommunityHome />} />
              <Route path="new" element={<CreateCommunity />} />
              <Route path="join/:code" element={<JoinCommunity />} />
              <Route path=":communityId" element={<CommunityPage />} />
              <Route
                path=":communityId/channels/:channelId"
                element={<CommunityPage />}
              />
              <Route
                path=":communityId/channels/:channelId/posts/:postId"
                element={<CommunityPage />}
              />
              <Route
                path=":communityId/settings"
                element={<CommunitySettings />}
              />
            </Route>

            {/* Games App */}
            <Route
              path="/games"
              element={
                <Suspense fallback={AuthFallback}>
                  <GamesHome />
                </Suspense>
              }
            />
            <Route
              path="/games/:gameId"
              element={
                <Suspense fallback={AuthFallback}>
                  <GameDetails />
                </Suspense>
              }
            />
            {/* Mimic pass-and-play (one device, fully local). */}
            <Route
              path="/games/mimic/local"
              element={
                <Suspense fallback={AuthFallback}>
                  <MimicLocal />
                </Suspense>
              }
            />
            {/* Standalone web-app games hosted in an iframe (Dancr, Rats). */}
            <Route
              path="/games/:gameId/play"
              element={
                <Suspense fallback={AuthFallback}>
                  <GameEmbed />
                </Suspense>
              }
            />
            <Route
              path="/games/mimic/menu"
              element={
                <Suspense fallback={AuthFallback}>
                  <MimicMenu />
                </Suspense>
              }
            />
            <Route
              path="/games/mimic/rooms/:roomId"
              element={
                <Suspense fallback={AuthFallback}>
                  <MimicRoom />
                </Suspense>
              }
            />
            <Route
              path="/games/blackjack/menu"
              element={
                <Suspense fallback={AuthFallback}>
                  <BlackjackMenu />
                </Suspense>
              }
            />
            <Route
              path="/games/blackjack/rooms/:roomId"
              element={
                <Suspense fallback={AuthFallback}>
                  <BlackjackTable />
                </Suspense>
              }
            />
            <Route
              path="/games/riddlr/play"
              element={
                <Suspense fallback={AuthFallback}>
                  <RiddlrPage />
                </Suspense>
              }
            />
            <Route
              path="/games/hilo/menu"
              element={
                <Suspense fallback={AuthFallback}>
                  <HiloMenu />
                </Suspense>
              }
            />
            <Route
              path="/games/hilo/rooms/:roomId"
              element={
                <Suspense fallback={AuthFallback}>
                  <HiloTable />
                </Suspense>
              }
            />
            <Route
              path="/games/tabletop/menu"
              element={
                <Suspense fallback={AuthFallback}>
                  <TabletopLobby />
                </Suspense>
              }
            />
            <Route
              path="/games/tabletop/rooms/:code"
              element={
                <Suspense fallback={AuthFallback}>
                  <TabletopTable />
                </Suspense>
              }
            />
            <Route
              path="/games/zingr/menu"
              element={
                <Suspense fallback={AuthFallback}>
                  <ZingrMenu />
                </Suspense>
              }
            />
            <Route
              path="/games/zingr/rooms/:roomId"
              element={
                <Suspense fallback={AuthFallback}>
                  <ZingrRoom />
                </Suspense>
              }
            />
            {/* Legacy URL redirects: Zingr was formerly /games/quiplip */}
            <Route path="/games/quiplip/menu" element={<Navigate to="/games/zingr/menu" replace />} />
            <Route path="/games/quiplip/rooms/:roomId" element={<QuiplipRoomRedirect />} />
            <Route
              path="/games/poker/menu"
              element={
                <Suspense fallback={AuthFallback}>
                  <PokerMenu />
                </Suspense>
              }
            />
            <Route
              path="/games/poker/rooms/:roomId"
              element={
                <Suspense fallback={AuthFallback}>
                  <PokerTable />
                </Suspense>
              }
            />
            <Route
              path="/games/doodlr/menu"
              element={
                <Suspense fallback={AuthFallback}>
                  <DoodlrMenu />
                </Suspense>
              }
            />
            <Route
              path="/games/doodlr/rooms/:roomId"
              element={
                <Suspense fallback={AuthFallback}>
                  <DoodlrRoom />
                </Suspense>
              }
            />
            <Route
              path="/games/bluffr/menu"
              element={
                <Suspense fallback={AuthFallback}>
                  <BluffrMenu />
                </Suspense>
              }
            />
            <Route
              path="/games/bluffr/rooms/:roomId"
              element={
                <Suspense fallback={AuthFallback}>
                  <BluffrRoom />
                </Suspense>
              }
            />
            {LEGACY_VILLAGR_PATHS.map((p) => <Route key={p} path={p} element={<SettlrRedirect />} />)}
            {/* Villagr — the PixiJS overworld game. */}
            <Route
              path="/games/villagr"
              element={
                <Suspense fallback={AuthFallback}>
                  <VillagrGame />
                </Suspense>
              }
            />

            <Route path="/" element={<Navigate to={window.location.hostname.startsWith("villagr-studio") ? "/villagr-studio" : "/feed"} replace />} />
            <Route path="*" element={<Navigate to="/feed" replace />} />
          </Route>
        </Route>
      </Routes>
      </ChunkErrorBoundary>
    </>
  );
}
